import {
  IWebsiteBuilderContainerBase,
  IWebsiteBuilderContainerDataBase,
  TWebsiteBuilderContainer,
} from "./websiteBuilderContainer";
import {
  websiteBuilderGenerateDefaultHideableNodeVisibility,
} from "../websiteBuilderHideableNode";
import { clamp } from "lodash";
import { CSSProperties } from "react";
import { isWebsiteBuilderIconAssetIdentity, IWebsiteBuilderIconAssetIdentity } from "../element/websiteBuilderIcon";
import { websiteBuilderNormalizeHexColor } from "../../schema/normalization/websiteBuilderNormalizeValue";

export const WEBSITE_BUILDER_TABLE_MAX_ROWS = 25;
export const WEBSITE_BUILDER_TABLE_MIN_CELLS = 1;
export const WEBSITE_BUILDER_TABLE_MAX_CELLS = 25;

export const WEBSITE_BUILDER_TABLE_HEAD_ROW_ID = "head-row";

interface IWebsiteBuilderTableCellTextValue {
  type: "text";
  content: string;
}

interface IWebsiteBuilderTableCellIconValue {
  type: "icon";
  assetIdentity: IWebsiteBuilderIconAssetIdentity;
  color: string;
}

export type TWebsiteBuilderTableCellValue =
  | IWebsiteBuilderTableCellTextValue
  | IWebsiteBuilderTableCellIconValue;

interface IWebsiteBuilderTableCellBase {
  id: string;
  value: TWebsiteBuilderTableCellValue;
}

export interface IWebsiteBuilderTableHeadCell extends IWebsiteBuilderTableCellBase {
  type: "head";
}

export interface IWebsiteBuilderTableBodyCell extends IWebsiteBuilderTableCellBase {
  type: "body";
}

export type TWebsiteBuilderTableCell = IWebsiteBuilderTableHeadCell | IWebsiteBuilderTableBodyCell;

interface IWebsiteBuilderTableRowBase<TCell extends TWebsiteBuilderTableCell> {
  id: string;
  type: string;
  backgroundColor?: string;
  color?: string;
  cells: TCell[];
}

export interface IWebsiteBuilderTableHeadRow extends IWebsiteBuilderTableRowBase<IWebsiteBuilderTableHeadCell> {
  id: typeof WEBSITE_BUILDER_TABLE_HEAD_ROW_ID;
  type: "head";
}

export interface IWebsiteBuilderTableBodyRow extends IWebsiteBuilderTableRowBase<IWebsiteBuilderTableBodyCell> {
  type: "body";
}

export type TWebsiteBuilderTableRow = IWebsiteBuilderTableHeadRow | IWebsiteBuilderTableBodyRow;

interface IWebsiteBuilderTableHead {
  row: IWebsiteBuilderTableHeadRow;
}

interface IWebsiteBuilderTableBody {
  rows: IWebsiteBuilderTableBodyRow[];
}

export interface IWebsiteBuilderTableSettings {
  head: IWebsiteBuilderTableHead;
  body: IWebsiteBuilderTableBody;
}

interface IWebsiteBuilderTableData extends IWebsiteBuilderContainerDataBase {
  type: "table";
  settings: IWebsiteBuilderTableSettings;
}

export interface IWebsiteBuilderTable extends IWebsiteBuilderContainerBase<[]> {
  container: IWebsiteBuilderTableData;
}

export function isWebsiteBuilderTable(node: TWebsiteBuilderContainer): node is IWebsiteBuilderTable {
  return node?.container?.type === "table";
}

function generateDefaultTableSettings(): IWebsiteBuilderTableSettings {
  return {
    head: {
      row: {
        id: WEBSITE_BUILDER_TABLE_HEAD_ROW_ID,
        type: "head",
        cells: [
          { id: crypto.randomUUID(), type: "head", value: { type: "text", content: "" } },
          { id: crypto.randomUUID(), type: "head", value: { type: "text", content: "" } },
          { id: crypto.randomUUID(), type: "head", value: { type: "text", content: "" } },
        ],
      },
    },
    body: {
      rows: [
        {
          id: crypto.randomUUID(),
          type: "body",
          cells: [
            { id: crypto.randomUUID(), type: "body", value: { type: "text", content: "" } },
            { id: crypto.randomUUID(), type: "body", value: { type: "text", content: "" } },
            { id: crypto.randomUUID(), type: "body", value: { type: "text", content: "" } },
          ],
        },
        {
          id: crypto.randomUUID(),
          type: "body",
          cells: [
            { id: crypto.randomUUID(), type: "body", value: { type: "text", content: "" } },
            { id: crypto.randomUUID(), type: "body", value: { type: "text", content: "" } },
            { id: crypto.randomUUID(), type: "body", value: { type: "text", content: "" } },
          ],
        },
      ],
    },
  };
}

export function websiteBuilderGenerateDefaultTable(): IWebsiteBuilderTable {
  return {
    id: crypto.randomUUID(),
    type: "container",
    container: {
      type: "table",
      layout: {
        desktop: {
          position: { x: 0, y: 0 },
          dimensions: { width: 350, height: 130 },
        },
        tablet: {
          position: { x: 0, y: 0 },
          dimensions: { width: 300, height: 130 },
        },
        mobile: {
          position: { x: 0, y: 0 },
          dimensions: { width: 250, height: 275 },
        },
      },
      visibility: websiteBuilderGenerateDefaultHideableNodeVisibility(),
      zIndex: 0,
      settings: generateDefaultTableSettings(),
    },
    children: [],
  };
}

export function websiteBuilderNormalizeTableSettings(partialSettings: Partial<IWebsiteBuilderTableSettings>): IWebsiteBuilderTableSettings {
  if (partialSettings == null) {
    return generateDefaultTableSettings();
  }

  const { head, body } = partialSettings;

  function normalizeCellValue(value: TWebsiteBuilderTableCellValue): TWebsiteBuilderTableCellValue {
    if (value?.type === "icon") {
      if (isWebsiteBuilderIconAssetIdentity(value.assetIdentity)) {
        return {
          type: "icon",
          assetIdentity: {
            providerCode: value.assetIdentity.providerCode,
            versionCode: value.assetIdentity.versionCode,
            variantCode: value.assetIdentity.variantCode,
            assetCode: value.assetIdentity.assetCode,
          },
          color: websiteBuilderNormalizeHexColor(value.color, null, false),
        };
      } else {
        return {
          type: "text",
          content: "",
        };
      }
    } else {
      return {
        type: "text",
        content: typeof value?.content === "string" ? value.content : "",
      };
    }
  }

  function normalizeHeadCell(cell: Partial<IWebsiteBuilderTableHeadCell>): IWebsiteBuilderTableHeadCell {
    return {
      id: typeof cell?.id === "string" ? cell.id : crypto.randomUUID(),
      type: "head",
      value: normalizeCellValue(cell?.value),
    };
  }

  function normalizeBodyCell(cell: Partial<IWebsiteBuilderTableBodyCell>): IWebsiteBuilderTableBodyCell {
    return {
      id: typeof cell?.id === "string" ? cell.id : crypto.randomUUID(),
      type: "body",
      value: normalizeCellValue(cell?.value),
    };
  }

  function normalizeHeadRow(row: Partial<IWebsiteBuilderTableHeadRow>, cellCount: number): IWebsiteBuilderTableHeadRow {
    const cells: IWebsiteBuilderTableHeadCell[] = [];

    for (let i = 0; i < cellCount; i++) {
      const cell = row?.cells?.[i];

      cells.push(normalizeHeadCell(cell));
    }

    return {
      id: WEBSITE_BUILDER_TABLE_HEAD_ROW_ID,
      type: "head",
      backgroundColor: typeof row?.backgroundColor === "string" ? row.backgroundColor : null,
      color: typeof row?.color === "string" ? row.color : null,
      cells,
    };
  }

  function normalizeBodyRow(row: Partial<IWebsiteBuilderTableBodyRow>, cellCount: number): IWebsiteBuilderTableBodyRow {
    const cells: IWebsiteBuilderTableBodyCell[] = [];

    for (let i = 0; i < cellCount; i++) {
      const cell = row?.cells?.[i];

      cells.push(normalizeBodyCell(cell));
    }

    return {
      id: typeof row?.id === "string" ? row.id : crypto.randomUUID(),
      type: "body",
      backgroundColor: typeof row?.backgroundColor === "string" ? row.backgroundColor : null,
      color: typeof row?.color === "string" ? row.color : null,
      cells,
    };
  }

  function getNormalizedCellCount(): number {
    if (Array.isArray(head?.row?.cells)) {
      return clamp(head.row.cells.length, WEBSITE_BUILDER_TABLE_MIN_CELLS, WEBSITE_BUILDER_TABLE_MAX_CELLS);
    } else if (Array.isArray(body?.rows?.[0]?.cells)) {
      return clamp(body.rows[0].cells.length, WEBSITE_BUILDER_TABLE_MIN_CELLS, WEBSITE_BUILDER_TABLE_MAX_CELLS);
    } else {
      return WEBSITE_BUILDER_TABLE_MIN_CELLS;
    }
  }

  const normalizedCellCount = getNormalizedCellCount();

  function normalizeHead(head: Partial<IWebsiteBuilderTableHead>): IWebsiteBuilderTableHead {
    return {
      row: normalizeHeadRow(head?.row, normalizedCellCount),
    };
  }

  function normalizeBody(body: Partial<IWebsiteBuilderTableBody>): IWebsiteBuilderTableBody {
    const rows = body?.rows;
    const normalizedRows: IWebsiteBuilderTableBodyRow[] = [];

    if (!Array.isArray(rows)) {
      return {
        rows: normalizedRows,
      };
    }

    for (let i = 0; i < rows.length && i < WEBSITE_BUILDER_TABLE_MAX_ROWS; i++) {
      const row = rows[i];
      normalizedRows.push(normalizeBodyRow(row, normalizedCellCount));
    }

    return {
      rows: normalizedRows,
    };
  }

  const normalizedTableSettings: IWebsiteBuilderTableSettings = {
    head: normalizeHead(head),
    body: normalizeBody(body),
  };

  return normalizedTableSettings;
}

interface IWebsiteBuilderTableDesignStyles {
  containerClassName: string;
  className: string;
}

export function websiteBuilderGenerateTableDesignStyles(): IWebsiteBuilderTableDesignStyles {
  return {
    containerClassName: "website-builder-table-container",
    className: "website-builder-table",
  };
}

interface IWebsiteBuilderTableRowDesignStyles {
  styleProperties: CSSProperties;
  styleDeclaration: string;
}

export function websiteBuilderGenerateTableRowDesignStyles(row: TWebsiteBuilderTableRow): IWebsiteBuilderTableRowDesignStyles {
  const styleProperties: CSSProperties = {
    backgroundColor: row.backgroundColor,
    color: row.color,
  };

  let styleDeclaration = "";

  if (styleProperties.backgroundColor != null) {
    styleDeclaration += `background-color: ${styleProperties.backgroundColor};`;
  }

  if (styleProperties.color != null) {
    styleDeclaration += `color: ${styleProperties.color};`;
  }
  
  return {
    styleProperties,
    styleDeclaration,
  };
}

interface IWebsiteBuilderTableCellDesignStyles {
  contentClassName: string;
  mobileContentContainerClassName: string;
  iconClassName: string;
  iconStyleProperties: CSSProperties;
  iconStyleDeclaration: string;
}

export function websiteBuilderGenerateTableCellDesignStyles(cell: TWebsiteBuilderTableCell): IWebsiteBuilderTableCellDesignStyles {
  const iconStyleProperties: CSSProperties = {
    color: cell.value.type === "icon" ? cell.value.color : null,
  };

  let iconStyleDeclaration = "";

  if (iconStyleProperties.color != null) {
    iconStyleDeclaration += `color: ${iconStyleProperties.color};`;
  }

  return {
    contentClassName: "website-builder-table-cell-content",
    mobileContentContainerClassName: "website-builder-table-cell-mobile-content-container",
    iconClassName: "website-builder-table-cell-icon",
    iconStyleProperties,
    iconStyleDeclaration,
  };
}